import { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { FileText, RotateCcw, Loader2 } from 'lucide-react'
import { EntityPageHeader, SearchInput, TablePagination } from '@/shared/components'
import { useAuditLogs } from '../hooks/use-audit-logs'
import type { AuditLogFilters } from '../types/audit-log'
import { cn } from '@/lib/utils'

const ACTIONS = ['LOGIN', 'LOGIN_FAILED', 'CREATE', 'UPDATE', 'DELETE'] as const
const ENTITY_TYPES = [
  'FORMATEUR', 'ETUDIANT', 'PROGRAMME', 'MATIERE',
  'TYPE_ACTIVITE', 'USER',
] as const

const PAGE_SIZE = 20

const ACTION_STYLES: Record<string, string> = {
  LOGIN: 'bg-violet-50 text-violet-700',
  LOGIN_FAILED: 'bg-orange-50 text-orange-700',
  CREATE: 'bg-emerald-50 text-emerald-700',
  UPDATE: 'bg-blue-50 text-blue-700',
  DELETE: 'bg-red-50 text-red-700',
  LOGOUT: 'bg-neutral-100 text-neutral-600',
}

function formatDateTime(iso: string): string {
  const date = new Date(iso)
  return date.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export function AdminLogsPage() {
  const { t } = useTranslation()

  const [search, setSearch] = useState('')
  const [actionFilter, setActionFilter] = useState('')
  const [entityTypeFilter, setEntityTypeFilter] = useState('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [page, setPage] = useState(0)

  const filters: AuditLogFilters = useMemo(() => ({
    page,
    size: PAGE_SIZE,
    ...(search && { search }),
    ...(actionFilter && { action: actionFilter }),
    ...(entityTypeFilter && { entityType: entityTypeFilter }),
    ...(dateFrom && { from: dateFrom + 'T00:00:00' }),
    ...(dateTo && { to: dateTo + 'T23:59:59' }),
  }), [page, search, actionFilter, entityTypeFilter, dateFrom, dateTo])

  const { data, isLoading } = useAuditLogs(filters)

  const handleReset = () => {
    setSearch('')
    setActionFilter('')
    setEntityTypeFilter('')
    setDateFrom('')
    setDateTo('')
    setPage(0)
  }

  const handleSearchChange = (value: string) => {
    setSearch(value)
    setPage(0)
  }

  const hasFilters = search || actionFilter || entityTypeFilter || dateFrom || dateTo

  return (
    <div className="space-y-6">
      <EntityPageHeader
        icon={FileText}
        title={t('admin.logs.title', "Journal d'activité")}
        subtitle={t('admin.logs.subtitle', 'Historique de toutes les actions sur la plateforme')}
      />

      {/* Filters */}
      <div className="rounded-xl border border-neutral-200 bg-white p-4 shadow-sm">
        <div className="flex flex-wrap items-end gap-3">
          <SearchInput
            value={search}
            onChange={handleSearchChange}
            placeholder={t('admin.logs.searchPlaceholder', 'Rechercher par email ou détail...')}
            className="w-64"
          />

          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-neutral-500">
              {t('admin.logs.filterAction', 'Action')}
            </label>
            <select
              value={actionFilter}
              onChange={(e) => { setActionFilter(e.target.value); setPage(0) }}
              className="h-10 rounded-lg border border-neutral-200 bg-white px-3 text-sm text-neutral-900 transition-colors hover:border-neutral-300 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/25"
            >
              <option value="">{t('admin.logs.allActions', 'Toutes')}</option>
              {ACTIONS.map((a) => (
                <option key={a} value={a}>
                  {t(`admin.logs.action.${a}`, a)}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-neutral-500">
              {t('admin.logs.filterEntity', 'Entité')}
            </label>
            <select
              value={entityTypeFilter}
              onChange={(e) => { setEntityTypeFilter(e.target.value); setPage(0) }}
              className="h-10 rounded-lg border border-neutral-200 bg-white px-3 text-sm text-neutral-900 transition-colors hover:border-neutral-300 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/25"
            >
              <option value="">{t('admin.logs.allEntities', 'Toutes')}</option>
              {ENTITY_TYPES.map((e) => (
                <option key={e} value={e}>
                  {t(`admin.logs.entity.${e}`, e)}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-neutral-500">
              {t('admin.logs.dateFrom', 'Du')}
            </label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => { setDateFrom(e.target.value); setPage(0) }}
              className="h-10 rounded-lg border border-neutral-200 bg-white px-3 text-sm text-neutral-900 transition-colors hover:border-neutral-300 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/25"
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-neutral-500">
              {t('admin.logs.dateTo', 'Au')}
            </label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => { setDateTo(e.target.value); setPage(0) }}
              className="h-10 rounded-lg border border-neutral-200 bg-white px-3 text-sm text-neutral-900 transition-colors hover:border-neutral-300 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/25"
            />
          </div>

          {hasFilters && (
            <button
              onClick={handleReset}
              className="flex h-10 items-center gap-2 rounded-lg border border-neutral-200 bg-white px-3 text-sm font-medium text-neutral-600 transition-colors hover:bg-neutral-50"
            >
              <RotateCcw className="h-3.5 w-3.5" />
              {t('admin.logs.reset', 'Réinitialiser')}
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-xl border border-neutral-200 bg-white shadow-sm">
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : !data || data.content.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-neutral-100">
              <FileText className="h-6 w-6 text-neutral-400" />
            </div>
            <p className="mt-3 text-sm font-medium text-neutral-900">
              {t('admin.logs.empty', 'Aucun log trouvé')}
            </p>
            <p className="mt-1 text-xs text-neutral-500">
              {hasFilters
                ? t('admin.logs.emptyFiltered', 'Essayez de modifier vos filtres.')
                : t('admin.logs.emptyAll', "Aucune activité n'a encore été enregistrée.")}
            </p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-neutral-200 bg-neutral-50/50">
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-neutral-500">
                      {t('admin.logs.col.date', 'Date')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-neutral-500">
                      {t('admin.logs.col.user', 'Utilisateur')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-neutral-500">
                      {t('admin.logs.col.action', 'Action')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-neutral-500">
                      {t('admin.logs.col.entity', 'Entité')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-neutral-500">
                      {t('admin.logs.col.detail', 'Détail')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-neutral-500">
                      {t('admin.logs.col.ip', 'IP')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-100">
                  {data.content.map((log) => (
                    <tr key={log.id} className="transition-colors hover:bg-neutral-50/50">
                      <td className="whitespace-nowrap px-4 py-3 text-neutral-600">
                        {formatDateTime(log.createdAt)}
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-neutral-900">{log.userEmail || '—'}</span>
                        {log.userRole && (
                          <span className="ml-1.5 text-xs text-neutral-400">
                            ({t(`admin.logs.role.${log.userRole}`, log.userRole)})
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className={cn(
                          'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium',
                          ACTION_STYLES[log.action] || 'bg-neutral-100 text-neutral-600',
                        )}>
                          {t(`admin.logs.action.${log.action}`, log.action)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-neutral-600">
                        {log.entityType
                          ? t(`admin.logs.entity.${log.entityType}`, log.entityType)
                          : '—'}
                      </td>
                      <td className="max-w-xs truncate px-4 py-3 text-neutral-600" title={log.detail || ''}>
                        {log.detail || '—'}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 font-mono text-xs text-neutral-400">
                        {log.ipAddress || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <TablePagination
              currentPage={data.number + 1}
              totalPages={data.totalPages}
              totalItems={data.totalElements}
              pageSize={data.size}
              onPageChange={(p) => setPage(p - 1)}
            />
          </>
        )}
      </div>
    </div>
  )
}
